import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  RgbaColor,
  selectEnvironmentInteractionPoints,
} from "../../app/slices/EnvironmentSlice";
import {
  closeSequencePositionPicker,
  selectSequencePositionPicker,
  selectSelectedTab,
  SelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectActors,
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedTake3D,
  selectSelectedTakeProps,
} from "../../app/slices/TrainingSlice";
import usePropHelper from "../../hooks/PropHelper";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import { Actor } from "../../models/Actor";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { IProximityTrigger } from "../../models/interactions/ProximityTrigger";
import { ISequence } from "../../models/interactions/Sequence";
import useUpdateSequenceInteraction from "../sequence/node_previews/UpdateSequenceInteraction.hook";
import InteractionPointPreview, {
  ActorPointStrategy,
  NpcPointStrategy,
  PlayerPointStrategy,
  PropPointStrategy,
} from "./InteractionPointPreview";
import {
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../sequence/EnvironmentPositionContext";

const selectedMarkerColor: RgbaColor = {
  r: 58 / 255,
  g: 196 / 255,
  b: 1,
  a: 1,
};

export default function Viewport3DSequenceProximityPositionLayer() {
  const dispatch = useDispatch();
  const currentTab = useSelector(selectSelectedTab);
  const picker = useSelector(selectSequencePositionPicker);
  const selectedElementType = useSelector(selectSelectedElementType);
  const sequence = useSelector(selectSelectedElement) as ISequence | undefined;
  const currentTake = useSelector(selectSelectedTake3D);
  const actors = useSelector(selectActors);
  const currentTakeProps = useSelector(selectSelectedTakeProps);
  const takePositions = useSelector(selectEnvironmentInteractionPoints) ?? [];
  const { getSituationMetadata } = useSituationHelper();
  const { getUsedPropByInstanceId } = usePropHelper();
  const { updateInteraction } = useUpdateSequenceInteraction();

  const activeInteraction = useMemo(() => {
    const node = sequence?.nodes?.find(
      (candidate) => candidate.id === picker.nodeId,
    );
    if (!node?.data || !("interaction" in node.data)) {
      return undefined;
    }

    return node.data.interaction as Interaction | undefined;
  }, [picker.nodeId, sequence?.nodes]);

  const isSequenceContext =
    selectedElementType === "takeSequence" ||
    (selectedElementType === "interaction" &&
      sequence?.type === InteractionType.Sequence);

  const isVisible =
    currentTab === SelectedTab.Timeline &&
    isSequenceContext &&
    picker.isOpen &&
    picker.positionType === "proximity" &&
    activeInteraction?.type === InteractionType.ProximityTrigger;

  const interaction = activeInteraction as IProximityTrigger | undefined;

  const actorByPositionId = new Map(
    (currentTake?.actors ?? [])
      .map((actorState) => {
        const actor = actors.find(
          (candidate) => candidate.id === actorState.actorId,
        );
        if (!actor) return undefined;
        return [actorState.positionId, actor] as const;
      })
      .filter((entry): entry is readonly [string, Actor] => !!entry),
  );

  const propStateByPositionId = new Map(
    (currentTakeProps ?? []).map((propTakeState) => [
      propTakeState.positionId,
      propTakeState,
    ]),
  );

  const positionContexts = getEnvironmentPositionContexts(
    takePositions,
    currentTake?.situations,
    getSituationMetadata,
  );

  const actorPositions = positionContexts.flatMap((context) =>
    context.actorPositions.map((actorPosition) => ({
      actorPosition,
      positionId: getSituationPositionId(context, actorPosition.id),
      takeColor: context.takeColor,
    })),
  );

  const propPositions = positionContexts.flatMap((context) =>
    context.propPositions.map((propPosition) => ({
      propPosition,
      positionId: getSituationPositionId(context, propPosition.id),
      takeColor: context.takeColor,
    })),
  );

  if (!isVisible || !interaction) {
    return null;
  }

  const getStrategyForActorPosition = (actor?: Actor) => {
    if (!actor) return NpcPointStrategy;
    if (actor.id === "player") return PlayerPointStrategy;
    return ActorPointStrategy;
  };

  const getBackgroundColor = (
    actor: Actor | undefined,
    takeColor?: RgbaColor,
  ) => {
    if (actor?.id === "player") return takeColor;
    return undefined;
  };

  const getMarkerBorderColor = (isSelected: boolean, takeColor?: RgbaColor) => {
    return isSelected ? selectedMarkerColor : takeColor;
  };

  const getActorMarkerBackgroundColor = (
    isSelected: boolean,
    actor: Actor | undefined,
    takeColor?: RgbaColor,
  ) => {
    if (isSelected) return selectedMarkerColor;
    return getBackgroundColor(actor, takeColor);
  };

  const getPropMarkerBackgroundColor = (isSelected: boolean) => {
    return isSelected ? selectedMarkerColor : undefined;
  };

  const getMarkerIconColor = (isSelected: boolean) => {
    return isSelected ? { r: 1, g: 1, b: 1, a: 1 } : undefined;
  };

  return (
    <>
      {actorPositions.map(({ actorPosition, positionId, takeColor }) => {
        const actor =
          actorByPositionId.get(positionId) ??
          actorByPositionId.get(actorPosition.id);
        const isSelected =
          interaction.posId === positionId ||
          interaction.posId === actorPosition.id;

        return (
          <InteractionPointPreview
            key={`actor-${positionId}`}
            strategy={getStrategyForActorPosition(actor)}
            x={actorPosition.position.x}
            y={actorPosition.position.y}
            actor={actor}
            markerBorderColor={getMarkerBorderColor(isSelected, takeColor)}
            markerBackgroundColor={getActorMarkerBackgroundColor(
              isSelected,
              actor,
              takeColor,
            )}
            markerIconColor={getMarkerIconColor(isSelected)}
            directionDegrees={actorPosition.direction?.x}
            directionColor={getMarkerBorderColor(isSelected, takeColor)}
            onClick={() => {
              updateInteraction(picker.nodeId, {
                ...interaction,
                posId: positionId,
              } as IProximityTrigger);
              dispatch(closeSequencePositionPicker());
            }}
          />
        );
      })}
      {propPositions.map(({ propPosition, positionId, takeColor }) => {
        const propTakeState =
          propStateByPositionId.get(positionId) ??
          propStateByPositionId.get(propPosition.id);
        const usedProp = propTakeState
          ? getUsedPropByInstanceId(propTakeState.instanceId)
          : undefined;
        const isSelected =
          interaction.posId === positionId ||
          interaction.posId === propPosition.id;

        return (
          <InteractionPointPreview
            key={`prop-${positionId}`}
            strategy={PropPointStrategy}
            x={propPosition.position.x}
            y={propPosition.position.y}
            prop={usedProp}
            markerBorderColor={getMarkerBorderColor(isSelected, takeColor)}
            markerBackgroundColor={getPropMarkerBackgroundColor(isSelected)}
            markerIconColor={getMarkerIconColor(isSelected)}
            directionDegrees={propPosition.direction?.x}
            directionColor={getMarkerBorderColor(isSelected, takeColor)}
            onClick={() => {
              updateInteraction(picker.nodeId, {
                ...interaction,
                posId: positionId,
              } as IProximityTrigger);
              dispatch(closeSequencePositionPicker());
            }}
          />
        );
      })}
    </>
  );
}
